import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Sparkles, X, Loader2, Wand2, Paperclip, FileText, FolderOpen, Camera, Image as ImageIcon } from 'lucide-react';
import CRMAttachmentPicker from './CRMAttachmentPicker';
import MobileCRMDocPicker from '../AdminMobile/MobileCRMDocPicker';
import { fetchCrmDocsAsFiles } from './crmDocUtils';
import { useKittGuard } from '../../hooks/useKittGuard';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

/**
 * Bouton + panneau d'assistant IA rédactionnel pour les emails.
 * Le panneau s'affiche comme la 2e page d'un livre : collé à droite de la fenêtre mail,
 * même taille, la paire étant recentrée à l'écran.
 */
export const AiEmailButton = ({ subject = '', currentBody = '', onInsert, fullscreen = false }) => {
  const { t, i18n } = useTranslation('admin');
  const guardKitt = useKittGuard();
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [proposal, setProposal] = useState('');
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]); // documents à analyser (upload)
  const [showCrmPicker, setShowCrmPicker] = useState(false); // sélecteur "Parcourir CRM"
  const [crmLoading, setCrmLoading] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null); // position/taille du panneau (mode "livre")
  const [attachMenuOpen, setAttachMenuOpen] = useState(false); // menu Photo/Galerie/Fichier (mobile)

  const btnRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const modalRef = useRef(null);        // fenêtre mail détectée
  const desiredTransformRef = useRef(null); // transform "livre" à imposer (calculé une seule fois)

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${backendUrl}/api/ai/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setAvailable(!!d.email_writing_enabled && !!d.email_writing_ready); })
      .catch(() => {});
  }, []);

  // Calcule UNE FOIS la position "livre" (à l'ouverture / au resize). Mesure la position NATURELLE
  // de la fenêtre mail (transform retiré), puis impose le décalage. Évite toute oscillation.
  const computeLayout = useCallback(() => {
    const modal = modalRef.current;
    const winW = window.innerWidth;
    if (!modal) {
      desiredTransformRef.current = null;
      setPanelStyle({ position: 'fixed', top: 0, right: 0, height: '100%', width: winW < 640 ? '100%' : 440 });
      return;
    }
    // Révéler la position naturelle (centrée) pour mesurer
    modal.style.removeProperty('transform');
    const base = window.getComputedStyle(modal).transform;
    const rect = modal.getBoundingClientRect();
    const W = rect.width;
    if (winW < 900 || W * 2 > winW - 24) {
      // Pas la place pour 2 pages : panneau docké à droite, fenêtre mail non décalée
      desiredTransformRef.current = null;
      setPanelStyle({ position: 'fixed', top: rect.top, right: 0, height: rect.height, width: winW < 640 ? '100%' : Math.min(W, winW - rect.left - 16) });
      return;
    }
    const targetLeft = winW / 2 - W;      // pour recentrer la double-page
    const shift = rect.left - targetLeft;  // décalage horizontal
    const baseStr = base && base !== 'none' ? base : '';
    const desired = `${baseStr} translate(${-shift}px, 0)`;
    desiredTransformRef.current = desired;
    modal.style.setProperty('transform', desired, 'important');
    setPanelStyle({ position: 'fixed', top: rect.top, left: winW / 2, width: W, height: rect.height });
  }, []);

  // À l'ouverture : détecter la fenêtre mail, appliquer le layout "livre" (une fois).
  useEffect(() => {
    if (!open) return;
    // Mode plein écran (mobile) : panneau de la taille de la fenêtre mail, par-dessus.
    if (fullscreen) {
      setPanelStyle({ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1250 });
      return;
    }
    const modal = btnRef.current ? btnRef.current.closest('.shadow-2xl') : null;
    modalRef.current = modal;
    if (modal) modal.style.transition = 'transform 0.25s ease';
    computeLayout();
    const onResize = () => computeLayout();
    window.addEventListener('resize', onResize);
    // Ré-impose (sans recalcul) si un re-render de la fenêtre mail a effacé le transform
    const iv = setInterval(() => {
      const m = modalRef.current;
      if (m && desiredTransformRef.current) {
        const cur = m.style.transform;
        if (!cur || cur === 'none') m.style.setProperty('transform', desiredTransformRef.current, 'important');
      }
    }, 400);
    return () => {
      window.removeEventListener('resize', onResize);
      clearInterval(iv);
      const m = modalRef.current;
      if (m) m.style.removeProperty('transform'); // restaurer la position centrée d'origine
    };
  }, [open, computeLayout, fullscreen]);

  if (!available) return null;

  const MAX_DOCS = 10;
  const MAX_DOC_BYTES = 40 * 1024 * 1024;

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setFiles((prev) => {
      const merged = [...prev];
      for (const f of incoming) {
        if (f.size > MAX_DOC_BYTES) { toast.error(t('mail.aiDocTooBig', { name: f.name })); continue; }
        if (merged.length >= MAX_DOCS) { toast.error(t('mail.aiDocTooMany', { max: MAX_DOCS })); break; }
        merged.push(f);
      }
      return merged;
    });
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleCrmDocs = async (selectedDocs) => {
    setShowCrmPicker(false);
    if (!selectedDocs || selectedDocs.length === 0) return;
    setCrmLoading(true);
    try {
      const token = localStorage.getItem('token');
      const crmFiles = await fetchCrmDocsAsFiles(backendUrl, token, selectedDocs);
      if (crmFiles.length === 0) { toast.error(t('mail.aiError')); return; }
      addFiles(crmFiles);
    } finally {
      setCrmLoading(false);
    }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const language = (i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';
      let res;
      if (files.length > 0) {
        // Analyse de documents : requête multipart
        const fd = new FormData();
        fd.append('draft', proposal || '');
        fd.append('subject', subject || '');
        fd.append('instruction', instruction || '');
        fd.append('language', language);
        files.forEach((f) => fd.append('files', f));
        res = await fetch(`${backendUrl}/api/ai/compose-email-docs`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      } else {
        res = await fetch(`${backendUrl}/api/ai/compose-email`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ draft: proposal || '', subject, instruction, language }),
        });
      }
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || t('mail.aiError')); return; }
      setProposal(data.draft || '');
      setInstruction('');
    } catch (e) {
      toast.error(t('mail.aiError'));
    } finally {
      setLoading(false);
    }
  };

  const insert = () => {
    if (proposal && onInsert) onInsert(proposal);
    setOpen(false);
    setProposal('');
    setFiles([]);
    toast.success(t('mail.aiInserted'));
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid="ai-email-btn"
        onClick={() => { if (guardKitt()) return; setOpen(true); }}
        title={t('mail.aiTitle') || "KITT — Rédigeons ensemble le mail"}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold bg-green-700 text-white hover:bg-green-600 transition-colors shadow-sm"
      >
        <Sparkles className="w-4 h-4" />
        KITT
      </button>

      {open && panelStyle && createPortal(
        <div
          className="z-[1250] bg-white shadow-2xl border-l border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-right duration-300"
          style={panelStyle}
          data-testid="ai-email-modal"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-violet-50 to-indigo-50 flex-shrink-0">
            <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
              <Sparkles className="w-5 h-5 text-violet-600" />
              {t('mail.aiTitle')}
            </h3>
            <button type="button" onClick={() => setOpen(false)} data-testid="ai-email-close" className="p-1 rounded-lg hover:bg-white/60">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto flex-1">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">{t('mail.aiPlaceholder')}</label>
              <textarea
                data-testid="ai-email-instruction"
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                rows={3}
                placeholder={t('mail.aiPlaceholder')}
                className="w-full text-sm border border-slate-200 rounded-lg p-3 focus:ring-2 focus:ring-violet-400 outline-none resize-none"
              />
            </div>

            {/* Documents à analyser */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-medium text-slate-500">{t('mail.aiDocsLabel')}</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="ai-email-browse-crm-btn"
                    onClick={() => setShowCrmPicker(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-rose-200 text-rose-700 hover:bg-rose-50 transition-colors"
                  >
                    <FolderOpen className="w-3.5 h-3.5" />
                    {t('mail.browseCrm')}
                  </button>
                  <button
                    type="button"
                    data-testid="ai-email-attach-btn"
                    onClick={() => { if (fullscreen) { setAttachMenuOpen(true); } else if (fileInputRef.current) { fileInputRef.current.click(); } }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-violet-200 text-violet-700 hover:bg-violet-50 transition-colors"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    {t('mail.aiAttach')}
                  </button>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                data-testid="ai-email-file-input"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                data-testid="ai-email-gallery-input"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                data-testid="ai-email-camera-input"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
              />
              {crmLoading && (
                <p className="text-xs text-rose-600 flex items-center gap-1.5" data-testid="ai-email-crm-loading">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('mail.browseCrm')}…
                </p>
              )}
              {files.length === 0 ? (
                <p className="text-xs text-slate-400">{t('mail.aiDocsHint')}</p>
              ) : (
                <ul className="space-y-1.5" data-testid="ai-email-files">
                  {files.map((f, idx) => (
                    <li
                      key={`${f.name}-${idx}`}
                      data-testid={`ai-email-file-${idx}`}
                      className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5"
                    >
                      <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="truncate flex-1 text-slate-700" title={f.name}>{f.name}</span>
                      <span className="text-slate-400">{Math.max(1, Math.round(f.size / 1024))} Ko</span>
                      <button
                        type="button"
                        data-testid={`ai-email-file-remove-${idx}`}
                        onClick={() => removeFile(idx)}
                        className="p-0.5 rounded hover:bg-slate-200 text-slate-500"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              type="button"
              onClick={generate}
              disabled={loading}
              data-testid="ai-email-generate"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {proposal ? t('mail.aiRegenerate') : t('mail.aiGenerate')}
            </button>

            {proposal && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-slate-500">{t('mail.aiTitle')}</p>
                <div
                  className="border border-slate-200 rounded-lg p-4 bg-slate-50 text-sm text-slate-700 prose prose-sm max-w-none whitespace-pre-wrap"
                  data-testid="ai-email-proposal"
                  dangerouslySetInnerHTML={{ __html: proposal }}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 px-5 py-4 border-t bg-slate-50 flex-shrink-0">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              {t('mail.aiCancel')}
            </button>
            <button
              type="button"
              onClick={insert}
              disabled={!proposal}
              data-testid="ai-email-insert"
              className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-60"
            >
              {t('mail.aiInsert')}
            </button>
          </div>
        </div>,
        document.body
      )}

      {attachMenuOpen && fullscreen && createPortal(
        <div className="fixed inset-0 z-[1350]" data-testid="ai-email-attach-menu">
          <div className="absolute inset-0 bg-black/50 animate-in fade-in duration-200" onClick={() => setAttachMenuOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-5 pb-8 shadow-2xl animate-in slide-in-from-bottom-6 duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-slate-800">{t('mail.aiAttach')}</h3>
              <button type="button" onClick={() => setAttachMenuOpen(false)} className="h-9 w-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <button type="button" data-testid="ai-attach-camera" onClick={() => { setAttachMenuOpen(false); cameraInputRef.current && cameraInputRef.current.click(); }} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-violet-50 text-violet-700 active:scale-95 transition-transform">
                <Camera size={26} /><span className="text-xs font-medium">{t('mobile.mailPhotoTake', 'Photo')}</span>
              </button>
              <button type="button" data-testid="ai-attach-gallery" onClick={() => { setAttachMenuOpen(false); galleryInputRef.current && galleryInputRef.current.click(); }} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-cyan-50 text-cyan-700 active:scale-95 transition-transform">
                <ImageIcon size={26} /><span className="text-xs font-medium">{t('mobile.mailPhotoGallery', 'Galerie')}</span>
              </button>
              <button type="button" data-testid="ai-attach-file" onClick={() => { setAttachMenuOpen(false); fileInputRef.current && fileInputRef.current.click(); }} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-slate-100 text-slate-700 active:scale-95 transition-transform">
                <FileText size={26} /><span className="text-xs font-medium">{t('mobile.mailAttachFile', 'Fichier')}</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showCrmPicker && (fullscreen
        ? createPortal(
            <MobileCRMDocPicker
              backendUrl={backendUrl}
              authToken={localStorage.getItem('token')}
              onConfirm={(docs) => handleCrmDocs(docs)}
              onClose={() => setShowCrmPicker(false)}
            />,
            document.body
          )
        : createPortal(
            <div style={{ position: 'fixed', inset: 0, zIndex: 1350 }} data-testid="ai-email-crm-picker">
              <CRMAttachmentPicker
                isOpen={showCrmPicker}
                onClose={() => setShowCrmPicker(false)}
                onSelectAttachments={handleCrmDocs}
                backendUrl={backendUrl}
                authToken={localStorage.getItem('token')}
              />
            </div>,
            document.body
          )
      )}
    </>
  );
};

export default AiEmailButton;
